import { AlertCircle, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { WarningNote } from "@/components/ui/warning-note";
import type { AnswerSaveFailure, AnswerSaveRefusal } from "@/lib/answerQueue";

/**
 * OPS-28 #1 — the review screens' "N answers didn't save" line, with the
 * retry that re-sends them. Shown only while a kept failure exists; the host
 * disables its Submit for as long as this is on screen, because a submit now
 * would grade those positions as blank while the candidate's screen says
 * they are answered.
 *
 * R13 (pre-ship 2026-10-18): a save the server refused twice is let go and
 * shown in `refused` instead — a warning that never holds the Submit. Except
 * where the submit itself requires the answer: a global test's /submit refuses
 * a Required question left unanswered (409 `required_unanswered`), so for those
 * positions (`requiredPositions`, the Review screen's) the note says to change
 * the answer instead of promising a submit (verify 2026-10-18).
 *
 * ⚠ Placeholder copy — the wording is Emilia's (owed.md).
 */
export function AnswerSaveFailureNote({
  failures,
  refused = [],
  requiredPositions = [],
  onRetry,
  retrying,
}: {
  failures: AnswerSaveFailure[];
  refused?: AnswerSaveRefusal[];
  /** Positions the submit still requires an answer for. */
  requiredPositions?: readonly number[];
  onRetry: () => void;
  retrying: boolean;
}) {
  const refusedNote = <RefusedNote refused={refused} requiredPositions={requiredPositions} />;
  if (failures.length === 0) return refusedNote;
  return (
    <>
      <FailedNote failures={failures} onRetry={onRetry} retrying={retrying} />
      {refusedNote}
    </>
  );
}

function RefusedNote({
  refused,
  requiredPositions,
}: {
  refused: AnswerSaveRefusal[];
  requiredPositions: readonly number[];
}) {
  if (refused.length === 0) return null;
  const list = (rs: AnswerSaveRefusal[]) => rs.map((r) => `Q${r.position}`).join(", ");
  const positions = list(refused);
  const blocked = refused.filter((r) => requiredPositions.includes(r.position));
  return (
    <WarningNote role="alert" className="flex items-start gap-2 text-sm">
      <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
      <span>
        {refused.length === 1
          ? `The answer to ${positions} couldn't be saved.`
          : `The answers to ${positions} couldn't be saved.`}{" "}
        {blocked.length > 0 ? (
          <>
            {list(blocked)} {blocked.length === 1 ? "is" : "are"} required, so the test
            can&rsquo;t be submitted until {blocked.length === 1 ? "it has an answer" : "they have answers"}{" "}
            that saves &mdash; try changing {blocked.length === 1 ? "it" : "them"}.
          </>
        ) : (
          <>
            You can still submit &mdash; {refused.length === 1 ? "it counts" : "they count"} as
            last saved, which may be unanswered.
          </>
        )}
      </span>
    </WarningNote>
  );
}

function FailedNote({
  failures,
  onRetry,
  retrying,
}: {
  failures: AnswerSaveFailure[];
  onRetry: () => void;
  retrying: boolean;
}) {
  const n = failures.length;
  const positions = failures.map((f) => `Q${f.position}`).join(", ");
  return (
    <div
      role="alert"
      className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-4"
    >
      <div className="flex items-start gap-3">
        <AlertCircle
          className="mt-0.5 h-5 w-5 flex-shrink-0 text-destructive"
          aria-hidden="true"
        />
        <div className="space-y-1">
          <p className="text-sm font-medium text-destructive">
            {n === 1 ? "1 answer didn't save" : `${n} answers didn't save`} ({positions}).
          </p>
          <p className="text-xs text-muted-foreground">
            Your answer is still here &mdash; press Retry to send it again. The
            test can&rsquo;t be submitted until it has saved.
          </p>
        </div>
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onRetry}
        disabled={retrying}
        className="cursor-pointer"
      >
        {retrying ? (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            Retrying…
          </>
        ) : (
          "Retry"
        )}
      </Button>
    </div>
  );
}
